// src/utils/useKeyboardInset.js
// TKT-2026-105056.
//
// A tablet's own keyboard slides over the page without changing the layout
// viewport, so a `position: fixed` centred MUI Dialog stays exactly where it
// was and the keyboard covers its lower half, which is where the checkout
// fields are. The browser reports the shrunken area on `window.visualViewport`,
// and that is the only reliable signal for it: there is no keyboard event.
//
// Returns how many pixels at the bottom of the screen are currently hidden.
// 0 means no keyboard (or a browser too old to tell us, where the behaviour is
// unchanged from before).
import { useEffect, useState } from 'react';

// Below this the shrink is browser chrome collapsing or an address bar moving,
// not a keyboard. Keyboards take a large share of a tablet screen.
const MIN_KEYBOARD_PX = 120;

export default function useKeyboardInset() {
    const [inset, setInset] = useState(0);

    useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!vv) return undefined;

        const measure = () => {
            // offsetTop matters on iOS, where the visual viewport is scrolled up
            // rather than shortened.
            const hidden = window.innerHeight - vv.height - vv.offsetTop;
            setInset(hidden > MIN_KEYBOARD_PX ? Math.round(hidden) : 0);
        };

        measure();
        vv.addEventListener('resize', measure);
        vv.addEventListener('scroll', measure);
        return () => {
            vv.removeEventListener('resize', measure);
            vv.removeEventListener('scroll', measure);
        };
    }, []);

    return inset;
}
